// src/modules/operaciones/inventarios/utils/constantes.js
//
// Constantes, helpers de bajo nivel, definiciones de la tabla y estilos compartidos del módulo de Ajustes.
// (Movido tal cual desde Invent.js: no se cambió ningún nombre ni valor.)
import { haceDias, isoDia, fmt, fmtFecha } from "../../../../utils/format"; // Fechas, números y orden compartidos

export const API = `${process.env.REACT_APP_API_URL || ""}/api/invent`; // URL base de la API de inventarios
export const POR_PAGINA = 25; // Filas por página en la tabla
export const DEBOUNCE_MS = 400; // Espera antes de recargar al cambiar fechas
export const LOTE_PRECIOS = 200; // Artículos por petición al calcular precios
export const SOLO_SI_COSTO_CERO = true; // Si es true, solo recalcula líneas con costo 0
export const DURACION_CHECK_MS = 2000; // tiempo que se muestra "✓ Listo" en el botón Ajustar
export const TIMEOUT_FETCH_MS = 120000; // 2 min: cubre lotes de 200 con SP lenta
export const TEXTO_ALERTA_EXCEL = "⚠️ REVISAR — Costo Promedio 0"; // Texto que se escribe en el Excel para alertas
export const CLAVE_VISTOS = "invent-vistos"; // localStorage: ids de ajustes marcados como revisados de forma permanente
export const NEUTRO_CHIP = "bg-white/70 hover:bg-white text-gray-800 border-white"; // Clases del chip de filtro inactivo
// Definición de los filtros de revisión con su clase cuando están activos
export const FILTROS_REVISION = [
  { id: "todos", label: "📋 Todos", activo: "ring-2 ring-sky-400/60 bg-sky-500/15 text-sky-800 border-sky-400" }, // Muestra todos
  { id: "pendientes", label: "⏳ Pendientes", activo: "ring-2 ring-amber-400/70 bg-amber-500/15 text-amber-800 border-amber-400" }, // Solo no revisados
  { id: "revisados", label: "✅ Revisados", activo: "ring-2 ring-emerald-400/70 bg-emerald-500/15 text-emerald-800 border-emerald-400" }, // Solo revisados
];

export const txt = (v) => (v === null || v === undefined ? "" : String(v).trim()); // Convierte a string recortado; null → ""
export const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0); // Convierte a número seguro; inválido → 0
export const lineasDe = (mapa, ajuste) => mapa.get(String(ajuste.AJUS_ID)) || []; // Obtiene las líneas de detalle de un ajuste
export const aFormatoFirebird = (v) => (v ? v.replace(/-/g, "/") : ""); // Cambia guiones por barras para el backend

// Detecta columnas que son identificadores (no se formatean con miles)
export const esIdentificador = (key) => /(_ID|_COD|_ITEM|_FACTOR|_UNIDAD|_LOTE)$/i.test(key);

// Formatea un valor de celda según su tipo y nombre de columna
export const fmtValor = (key, v) => {
  if (v === null || v === undefined || txt(v) === "") return "—"; // Vacío: muestra guion
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) return fmtFecha(v); // Strings ISO con hora: formatea fecha
  if (typeof v === "number") return esIdentificador(key) ? String(v) : fmt(v, 3); // Números: ids sin formato, el resto con miles
  if (typeof v === "object") return JSON.stringify(v); // Objetos: los serializa
  return txt(v); // Resto: texto limpio
};

// Atajos de rango de fechas; cada uno calcula [inicio, fin]
export const RANGOS_RAPIDOS = [
  { id: "hoy", label: "Hoy", calc: () => [new Date(), new Date()] }, // Solo hoy
  { id: "7d", label: "Últimos 7 días", calc: () => [haceDias(7), new Date()] }, // Última semana
  { id: "30d", label: "Últimos 30 días", calc: () => [haceDias(30), new Date()] }, // Último mes móvil
  {
    id: "mes", // Mes en curso
    label: "Este mes", // Texto del botón
    calc: () => {
      const h = new Date(); // Fecha actual
      return [new Date(h.getFullYear(), h.getMonth(), 1), h]; // Del día 1 hasta hoy
    },
  },
  {
    id: "mesPasado", // Mes anterior completo
    label: "Mes pasado", // Texto del botón
    calc: () => {
      const h = new Date(); // Fecha actual
      return [new Date(h.getFullYear(), h.getMonth() - 1, 1), new Date(h.getFullYear(), h.getMonth(), 0)]; // Día 1 al último del mes pasado
    },
  },
  { id: "limpiar", label: "Limpiar fechas", calc: () => [null, null] }, // Quita ambas fechas
];

/* ---- Tabla de ajustes ---- */

// Columnas de la tabla principal con su clave, etiqueta y alineación
export const COLUMNAS = [
  { key: "id", label: "ID", align: "text-center" }, // Columna ID
  { key: "fecha", label: "Fecha", align: "text-center" }, // Columna fecha
  { key: "bode", label: "Bodega", align: "text-center" }, // Columna bodega
  { key: "conc", label: "Concepto", align: "text-left" }, // Columna concepto
  { key: "estado", label: "Estado", align: "text-center" }, // Columna estado
];

// Funciones que extraen el valor de ordenamiento de cada columna
export const ORDEN = {
  id: (a) => a.AJUS_ID, // Ordena por id numérico
  fecha: (a) => isoDia(a.AJUS_FECHA), // Ordena por día ISO
  bode: (a) => txt(a.BODE_COD), // Ordena por bodega
  conc: (a) => txt(a.AJUS_CONC), // Ordena por concepto
  estado: (a) => txt(a.AJUS_ANULADO), // Ordena por estado anulado
};

// Filtros de estado con etiqueta y función que decide si un ajuste pasa
export const ESTADOS = {
  todos: { label: "Todos", pasa: () => true }, // Todos pasan
  activos: { label: "Activos", pasa: (a) => txt(a.AJUS_ANULADO) === "N" }, // Solo no anulados
  anulados: { label: "Anulados", pasa: (a) => txt(a.AJUS_ANULADO) === "S" }, // Solo anulados
};

/* ============================ Estilos ============================ */

// Clases de cada tipo de banner de mensaje
export const BANNER = {
  success: "bg-emerald-500/10 text-emerald-900 border border-emerald-500/20", // Éxito en verde
  warning: "bg-amber-500/10 text-amber-900 border border-amber-500/20", // Advertencia en ámbar
  error: "bg-red-500/10 text-red-900 border border-red-500/20", // Error en rojo
  info: "bg-blue-500/10 text-blue-900 border border-blue-500/20", // Info en azul
};

// Clases del botón primario azul
export const BTN_PRIMARY =
  "px-5 py-2.5 rounded-xl bg-[#007aff] hover:bg-[#005bb5] text-white text-xs font-bold transition-all shadow-md cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed";

// Genera clases de botón e input que dependen del tema activo
export const estilosTema = (tema) => ({
  btnGlass: `px-5 py-2.5 rounded-xl ${tema.btnBg} ${tema.btnTexto} text-xs font-bold border ${tema.btnBorde} shadow-sm cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed transition-all`, // Botón estilo cristal
  inputGlass: `w-full px-4 py-2.5 rounded-2xl border ${tema.inputBorde} ${tema.inputBg} text-xs shadow-inner focus:outline-none font-medium ${tema.textoNormal} placeholder:text-gray-400`, // Input estilo cristal
});